// use-ha-connection.jsx — top-level React hook for the standalone PWA.
//
// Orchestrates: acquire auth → open WS → subscribe entities → expose
// a `hass`-shaped object the rest of the app uses. Returns a `status`
// field so the entry component can render auth / loading / error
// states before the panel mounts.
//
// First-visit flow on LAN:
//   1. PWA loads, useHaConnection auto-runs.
//   2. No tokens stored → library redirects browser to HA's
//      /auth/authorize.
//   3. HA's auth_trusted_networks auto-grants (silent).
//   4. Browser bounces back to the PWA with ?code=...&auth_callback=1.
//   5. Library exchanges code for tokens, persists to localStorage.
//   6. WebSocket opens, entities subscribed, panel renders.
//
// Subsequent visits: tokens are in localStorage → skip the redirect
// entirely. Panel renders directly.
//
// Off-LAN (Nabu Casa relay): trusted_networks doesn't auto-grant, so
// HA shows its login UI. User enters password once. Refresh token
// then covers ~30 days of activity.
//
// Beyond boot, the hook also tracks connection health (`connState`)
// via the liveness watcher — iOS suspends the socket when the PWA is
// backgrounded, and without an explicit probe-on-wake the first taps
// after picking the device up go into a dead socket — and surfaces
// rejected service calls (`serviceError`) so a failed press is never
// silent.

import { useEffect, useRef, useState } from 'react';
import {
  ERR_HASS_HOST_REQUIRED,
  ERR_INVALID_AUTH,
  ERR_CONNECTION_LOST,
} from 'home-assistant-js-websocket';

import { acquireAuth, cleanOAuthUrl, signOut, clearStoredTokens } from './auth.js';
import { openConnection, buildHass } from './connection.js';
import { attachLiveness } from './liveness.js';
import { buildWatchSet, watchedChanged } from './watch-set.js';
import { MAPPINGS, SONOS_SPEAKERS } from '../hooks/use-panel-rooms.jsx';
import { LAUNDRY_ENTITIES } from '../power/laundry.js';

const STATUS = {
  BOOTING: 'booting',
  AUTHENTICATING: 'authenticating',
  CONNECTING: 'connecting',
  READY: 'ready',
  ERROR: 'error',
};

const SERVICE_ERROR_DISMISS_MS = 6000;

// Heartbeat re-render, so a wrong watch set degrades to "briefly stale"
// rather than "stale until reload". 10s is well under the threshold at
// which anyone would look at a tile and believe it.
const RESYNC_MS = 10000;

// Escape hatch for the iPads via Safari remote inspector:
//   localStorage.setItem('__noRenderFilter', '1'); location.reload();
// Restores the old render-on-everything behaviour without a deploy, so
// if a tile ever looks stuck this can be ruled in or out in seconds.
const filterDisabled = () => {
  try {
    return window.localStorage?.getItem('__noRenderFilter') === '1';
  } catch {
    return false;                       // private mode / storage denied
  }
};

// Rejections arrive in three shapes: the bare ERR_CONNECTION_LOST
// constant (sendMessage on a downed socket), {code, message} objects
// (in-flight commands rejected on close, and HA service errors), and
// plain Errors. Normalise to something a family member can read.
function describeServiceError(err, { domain, service }) {
  const action = `${domain}.${service}`;
  if (err === ERR_CONNECTION_LOST || err?.code === ERR_CONNECTION_LOST) {
    return {
      message: "No connection to Home Assistant — that tap didn't go through.",
      detail: action,
    };
  }
  return {
    message: err?.message ? String(err.message) : 'Home Assistant rejected the command.',
    detail: action,
  };
}

export function useHaConnection() {
  const [status, setStatus] = useState(STATUS.BOOTING);
  const [hass, setHass] = useState(null);
  const [error, setError] = useState(null);
  const [connState, setConnState] = useState('connected');
  const [serviceError, setServiceError] = useState(null);
  const teardown = useRef(null);
  const errTimer = useRef(null);
  // Re-render bumper. Entity updates mutate the same hass facade but
  // replace `states`; we bump a counter to force React renders. Keeps
  // hass identity stable while signalling state changes.
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const showServiceError = (err, ctx) => {
      if (cancelled) return;
      setServiceError({ ts: Date.now(), ...describeServiceError(err, ctx) });
      clearTimeout(errTimer.current);
      errTimer.current = setTimeout(() => setServiceError(null), SERVICE_ERROR_DISMISS_MS);
    };

    (async () => {
      try {
        setStatus(STATUS.AUTHENTICATING);
        const auth = await acquireAuth();
        if (cancelled) return;
        cleanOAuthUrl();

        setStatus(STATUS.CONNECTING);
        const connection = await openConnection(auth);
        if (cancelled) return;

        // Only re-render when an entity the panel actually reads has
        // moved. Measured before writing this: ~97% of updates were for
        // entities that appear nowhere in the UI (see pwa/watch-set.js
        // for the numbers). Everything here fails OPEN — any doubt and
        // we render, because the cost of over-rendering is the status
        // quo whereas the cost of under-rendering is a silently stale
        // tile.
        // Laundry hold: so Run anyway and a plug switching off show at once
        // rather than on the 10 s heartbeat.
        const watch = buildWatchSet(MAPPINGS, SONOS_SPEAKERS, LAUNDRY_ENTITIES);
        let prevStates = null;
        const bump = () => setTick((t) => t + 1);

        // Safety net: render on a slow heartbeat regardless, so if the
        // watch set is ever wrong the worst case is a tile up to
        // RESYNC_MS stale rather than stale forever. Cheap next to the
        // ~70 renders/minute this replaces.
        const resync = setInterval(bump, RESYNC_MS);

        const { hass: facade, unsubscribe } = buildHass(auth, connection, {
          onUpdate: (f, reason) => {
            // Config updates are rare and never filtered.
            if (reason !== 'entities') { bump(); return; }
            const next = f.states;
            const changed = filterDisabled() || watchedChanged(prevStates, next, watch);
            prevStates = next;
            if (changed) bump();
          },
          onServiceError: showServiceError,
        });
        const detachLiveness = attachLiveness(connection, {
          onStateChange: (state) => {
            if (!cancelled) setConnState(state);
          },
          onAuthFailure: () => {
            // Token revoked while suspended — same recovery as the
            // boot-time ERR_INVALID_AUTH path below.
            clearStoredTokens();
            window.location.reload();
          },
        });
        teardown.current = () => {
          clearInterval(resync);
          detachLiveness();
          unsubscribe();
          connection.close();
        };
        // Debug affordance (Safari remote inspector on the iPads):
        // mirrors HA frontend exposing its connection globally.
        window.__haConn = connection;
        window.__hass = facade;

        setHass(facade);
        setStatus(STATUS.READY);
      } catch (err) {
        if (cancelled) return;
        if (err === ERR_HASS_HOST_REQUIRED) {
          setError(new Error('VITE_HASS_URL not configured'));
        } else if (err === ERR_INVALID_AUTH) {
          setError(new Error('Saved auth is invalid — clearing and retrying'));
          clearStoredTokens();
          setTimeout(() => window.location.reload(), 500);
          return;
        } else {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        setStatus(STATUS.ERROR);
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(errTimer.current);
      if (teardown.current) teardown.current();
    };
  }, []);

  return {
    status,
    hass,
    error,
    connState,
    serviceError,
    dismissServiceError: () => {
      clearTimeout(errTimer.current);
      setServiceError(null);
    },
    signOut: () => signOut(hass?.auth),
  };
}

export { STATUS };
